import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Select, MenuItem,
  IconButton, FormControl, InputLabel, Chip,
} from '@mui/material';
import ExpiryDateTimePicker from '../../ExpiryDateTimePicker';
import { Plus, Trash2, Pencil, GripVertical } from 'lucide-react';
import { RichTextEditor } from '../../RichTextEditor/RichTextEditor';

const navy = '#1e3a5f';

const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const buildEmpty = (fields) =>
  fields.reduce((acc, f) => ({ ...acc, [f.key]: f.defaultValue ?? '' }), {});

const stripHtml = (html) => html?.replace(/<[^>]*>/g, '').trim() || '';

const ItemForm = ({ fields, value, onChange, onSave, onCancel, isEditing }) => (
  <Box sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2, mb: 1.5 }}>
    <Stack spacing={1.5}>
      {fields.map((field) => {
        if (field.showWhen && value[field.showWhen.field] !== field.showWhen.value) return null;

        if (field.type === 'select') {
          return (
            <FormControl key={field.key} size="small" fullWidth>
              <InputLabel sx={{ fontSize: '0.82rem' }}>{field.label}</InputLabel>
              <Select
                label={field.label}
                value={value[field.key] || ''}
                onChange={(e) => onChange({ ...value, [field.key]: e.target.value })}
                sx={{ ...inputSx, fontSize: '0.82rem', bgcolor: 'white' }}
              >
                {(field.options || []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.82rem' }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        if (field.type === 'richtext') {
          return (
            <Box key={field.key}>
              {field.label && (
                <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 0.75 }}>
                  {field.label}
                </Typography>
              )}
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'white' }}>
                <RichTextEditor
                  initialHtml={value[field.key] || ''}
                  onChange={(html) => onChange({ ...value, [field.key]: html })}
                />
              </Box>
            </Box>
          );
        }

        if (field.type === 'datetime-local') {
          return (
            <ExpiryDateTimePicker
              key={field.key}
              label={field.label}
              value={value[field.key] || ''}
              onChange={(iso) => onChange({ ...value, [field.key]: iso })}
              slotPropsTextField={{ sx: { ...inputSx, '& .MuiInputLabel-root': { fontSize: '0.82rem' }, bgcolor: 'white' } }}
            />
          );
        }

        if (field.type === 'wordBoldSelector') {
          const text     = value[field.linkedField] || '';
          const words    = text.trim().split(/\s+/).filter(Boolean);
          const boldList = value[field.key] || [];
          if (words.length === 0) return (
            <Typography key={field.key} sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>
              {field.label} — הזן טקסט בשדה הסוג לבחירת מילים להדגשה
            </Typography>
          );
          return (
            <Box key={field.key}>
              <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600, mb: 0.75 }}>
                {field.label}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {words.map((word, i) => {
                  const bold = boldList.includes(word);
                  return (
                    <Chip
                      key={i}
                      label={word}
                      size="small"
                      onClick={() => {
                        const next = bold
                          ? boldList.filter(w => w !== word)
                          : [...boldList, word];
                        onChange({ ...value, [field.key]: next });
                      }}
                      sx={{
                        fontWeight: bold ? 700 : 400,
                        bgcolor: bold ? '#1e3a5f' : 'white',
                        color:   bold ? 'white'   : '#374151',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: bold ? '#152844' : '#f9fafb' },
                      }}
                    />
                  );
                })}
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.5 }}>
                לחץ על מילה להדגשתה בתצוגה
              </Typography>
            </Box>
          );
        }

        if (field.type === 'color') {
          return (
            <Stack key={field.key} direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ fontSize: '0.82rem', color: '#374151', minWidth: 80 }}>
                {field.label}
              </Typography>
              <Box
                component="input"
                type="color"
                value={value[field.key] || '#1e3a5f'}
                onChange={(e) => onChange({ ...value, [field.key]: e.target.value })}
                style={{ width: 40, height: 34, border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', padding: 2 }}
              />
              <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                {value[field.key] || '#1e3a5f'}
              </Typography>
            </Stack>
          );
        }

        return (
          <TextField
            key={field.key}
            size="small"
            label={field.label}
            placeholder={field.placeholder || ''}
            type={field.type || 'text'}
            multiline={field.type === 'textarea'}
            minRows={field.type === 'textarea' ? 2 : undefined}
            value={value[field.key] ?? ''}
            onChange={(e) => onChange({ ...value, [field.key]: field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })}
            fullWidth
            required={field.required}
            sx={{ ...inputSx, '& .MuiInputLabel-root': { fontSize: '0.82rem' }, fontSize: '0.82rem', bgcolor: 'white' }}
          />
        );
      })}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!value[fields.find(f => f.required)?.key || fields[0]?.key]}
          sx={{
            bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
            boxShadow: 'none', borderRadius: '8px',
            '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
          }}
        >
          {isEditing ? 'עדכן' : 'הוסף'}
        </Button>
      </Stack>
    </Stack>
  </Box>
);

const ItemSummary = ({ fields, item }) => {
  const primary = fields[0];
  const secondary = fields.slice(1).find(f => item[f.key]);
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {stripHtml(item[primary?.key]) || item[primary?.key] || '—'}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {secondary.type === 'color'
            ? <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', bgcolor: item[secondary.key], border: '1px solid #e5e7eb', verticalAlign: 'middle', ml: 0.5 }} />
            : secondary.type === 'datetime-local' && item[secondary.key]
            ? new Date(item[secondary.key]).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })
            : item[secondary.key]}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * GenericListStep
 * רשימה גנרית — מודעות, אנשי קשר, שמחות, קטגוריות הוצאות
 *
 * Props:
 *   fieldKey    string   — מפתח ב-formData (e.g. 'notices')
 *   addLabel    string   — "הוסף מודעה"
 *   emptyLabel  string   — "אין מודעות עדיין"
 *   fields      [{ key, label, type, placeholder, required, options, defaultValue }]
 *   formData    object
 *   onChange    (partial) => void
 */
const GenericListStep = ({
  fieldKey = 'items',
  addLabel = 'הוסף פריט',
  emptyLabel = 'אין פריטים עדיין',
  fields = [],
  formData,
  onChange,
}) => {
  const items = formData?.[fieldKey] || [];
  const [draft, setDraft] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const isOpen = draft !== null;

  const updateItems = (updated) => onChange({ [fieldKey]: updated });
  const removeItem = (index) => {
    updateItems(items.filter((_, i) => i !== index));
    if (editingIndex === index) closeDraft();
  };

  const openAdd = () => {
    setEditingIndex(null);
    setDraft(buildEmpty(fields));
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setDraft({ ...items[index] });
  };

  const closeDraft = () => {
    setDraft(null);
    setEditingIndex(null);
  };

  const saveDraft = () => {
    const requiredField = fields.find(f => f.required);
    if (requiredField) {
      const val = draft[requiredField.key];
      const isEmpty = requiredField.type === 'richtext' ? !stripHtml(val) : !val?.trim();
      if (isEmpty) return;
    }
    if (editingIndex !== null) {
      updateItems(items.map((item, i) => i === editingIndex ? { ...draft } : item));
    } else {
      updateItems([...items, { ...draft }]);
    }
    closeDraft();
  };

  return (
    <Box dir="rtl">
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Right side (RTL): list */}
        <Box sx={{ flex: isOpen ? '0 0 40%' : 1, minWidth: 0, transition: 'flex 0.2s' }}>
          <Button
            startIcon={<Plus size={15} />}
            onClick={openAdd}
            disabled={isOpen}
            sx={{
              border: '1.5px dashed #d1d5db',
              borderRadius: '10px', color: '#6b7280', textTransform: 'none',
              fontSize: '0.8rem', px: 2, py: 0.75, width: '100%', mb: 1.5,
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
              '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#c0c0c0' },
            }}
          >
            {addLabel}
          </Button>

          {items.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)' }}>
              {items.map((item, i) => {
                const isActive = editingIndex === i;
                return (
                  <Box key={i} sx={{
                    px: 1.5, py: 1,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: isActive ? '#eff6ff' : i % 2 === 0 ? '#fafafa' : 'white',
                    borderBottom: i < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                    borderRight: isActive ? `3px solid ${navy}` : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}>
                    <GripVertical size={13} color="#d1d5db" style={{ flexShrink: 0 }} />
                    <ItemSummary fields={fields} item={item} />
                    <IconButton size="small" onClick={() => startEdit(i)}
                      sx={{ color: isActive ? navy : '#9ca3af', '&:hover': { color: navy }, p: 0.5 }}>
                      <Pencil size={13} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeItem(i)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : !isOpen && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              {emptyLabel}
            </Typography>
          )}
        </Box>

        {/* Left side (RTL): form */}
        {isOpen && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ItemForm
              key={editingIndex ?? 'new'}
              fields={fields}
              value={draft}
              onChange={setDraft}
              onSave={saveDraft}
              onCancel={closeDraft}
              isEditing={editingIndex !== null}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GenericListStep;
